import {
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
  Spin,
  message,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import {
  getPublicAccount,
  createPublicSubscription,
} from "../../../services/public.service";
import { formatCurrency } from "../../../services/utils";
import { frequencies } from "../../../Enums/Frequency.enums";
import { paymentMethods } from "../../../Enums/PaymentMethod.enums";
import InputMask from "react-input-mask";
import { findAddressByCep } from "../../../services/viacep.service";
import { useState } from "react";
import { AlertService } from "../../../services/alert.service";

const { Text, Title } = Typography;
const { Panel } = Collapse;

export default function PublicPlanCheckout() {
  const { accountUuid, planUuid } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [documentType, setDocumentType] = useState<string>("CPF");
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<any[]>(
    []
  );
  const [account, setAccount] = useState<any>(undefined);
  const [plan, setPlan] = useState<any>(undefined);

  const paymentMethod = Form.useWatch("payment_method", form);

  const { isLoading: loadingAccount, isError: isErrorAccount } = useQuery(
    ["loadPublicAccount", accountUuid],
    () => (accountUuid ? getPublicAccount(accountUuid) : null),
    {
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess: (response) => {
        const acc = response.data || {};
        const settings =
          typeof acc.settings === "string"
            ? JSON.parse(acc.settings)
            : acc.settings || {};

        const accountMethods = settings.payment_methods || [];
        const filteredMethods = paymentMethods.filter((pm) =>
          accountMethods.includes(pm.value)
        );
        setAvailablePaymentMethods(filteredMethods);

        if (filteredMethods.length > 0) {
          form.setFieldValue("payment_method", filteredMethods[0].value);
        }

        setAccount(acc);
        setPlan(acc?.plans?.find((plan: any) => plan?.uuid === planUuid));
      },
    }
  );

  const { mutate: subscribe } = useMutation(
    (data: any) => createPublicSubscription(accountUuid!, data),
    {
      onSuccess: () => {
        message.success("Assinatura realizada com sucesso!");
        navigate(`/plans/${accountUuid}`);
      },
      onError: (error: any) => {
        setLoadingSubmit(false);
      },
    }
  );

  const onFinish = () => {
    form
      .validateFields()
      .then((values) => {
        setLoadingSubmit(true);

        const payload: any = {
          plan_uuid: planUuid,
          customer: {
            name: values.name,
            email: values.email,
            document:
              values.document_type === "CPF"
                ? values.document.replace("_", "").substring(0, 14)
                : values.document.replace("_", "").substring(0, 18),
            phone: values.phone.replace("_", "").substring(0, 15),
            postal_code: values.postal_code.replace("_", "").substring(0, 9),
            address: `${values.address}, ${values.number}`,
            neighborhood: values.neighborhood,
            city: values.city,
            state: values.state,
          },
          contract: {
            payment_method: values.payment_method,
          },
        };

        if (values.payment_method === "CreditCard") {
          payload.credit_card = {
            holder_name: values.card_holder_name,
            number: values.card_number.replace("_", "").substring(0, 19),
            expiration_date: values.card_expiration_date
              .replace("_", "")
              .substring(0, 5),
            cvv: values.card_cvv.replace("_", "").substring(0, 3),
          };
        }

        subscribe(payload);
      })
      .catch(() =>
        AlertService.sendAlert([
          { text: "Campo(s) com preenchimento inválido", type: "error" },
        ])
      );
  };

  const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const cep = e.target.value.replace(/\D/g, "");
    if (cep.length === 8) {
      try {
        const response = await findAddressByCep(cep);
        if (response.data) {
          form.setFieldsValue({
            address: response.data.logradouro,
            neighborhood: response.data.bairro,
            city: response.data.localidade,
            state: response.data.uf,
          });
        }
      } catch (error) {
        console.error("Erro ao buscar CEP", error);
      }
    }
  };

  if (loadingAccount) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (isErrorAccount || !account || !account.uuid) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
        }}
      >
        <Title level={4}>Conta não encontrada ou link inválido.</Title>
        <Button onClick={() => navigate(`/plans/${accountUuid}`)}>
          Voltar
        </Button>
      </div>
    );
  }

  if (!plan.id) {
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        <Title level={4}>Plano não encontrado</Title>
        <Button onClick={() => navigate(`/plans/${accountUuid}`)}>
          Voltar
        </Button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "20px" }}>
      <Card
        styles={{
          header: { color: "#107B99", fontWeight: 600 },
          body: { padding: 0 },
        }}
        title="Finalizar Contratação"
      >
        <div style={{ padding: 24 }}>
          <Row gutter={16} align="middle" justify="center">
            <Col span={24} style={{ textAlign: "center", marginBottom: 24 }}>
              <Space direction="vertical" size={2}>
                {account?.thumbnail && (
                  <img
                    src={account?.thumbnail}
                    alt={account?.name}
                    style={{
                      height: 60,
                      objectFit: "contain",
                      marginBottom: 8,
                    }}
                  />
                )}
                <Title level={4} style={{ margin: 0 }}>
                  {account?.name}
                </Title>
              </Space>
            </Col>
          </Row>

          <Divider />

          <Row gutter={24}>
            <Col xs={24} md={8} order={2}>
              <Card
                title="Resumo do Pedido"
                bordered={false}
                style={{ background: "#f5f5f5" }}
              >
                <Title level={5}>{plan.name}</Title>
                <Text type="secondary">
                  {frequencies.find((f) => f.value === plan.frequency)?.label}
                </Text>
                <Divider style={{ margin: "12px 0" }} />
                <Row justify="space-between">
                  <Text strong>Total:</Text>
                  <Title level={4} style={{ margin: 0, color: "#107B99" }}>
                    {formatCurrency(plan.price)}
                  </Title>
                </Row>
              </Card>
            </Col>

            <Col xs={24} md={16} order={1}>
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                autoComplete="off"
              >
                <Collapse defaultActiveKey={["1"]} style={{ marginBottom: 24 }}>
                  <Panel header="Seus Dados" key="1">
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          label="Nome Completo / Razão Social"
                          name="name"
                          rules={[
                            {
                              required: true,
                              message: "Por favor informe seu nome",
                            },
                          ]}
                        >
                          <Input size="large" />
                        </Form.Item>
                      </Col>
                      <Col
                        lg={{ span: 12 }}
                        md={{ span: 12 }}
                        sm={{ span: 24 }}
                        xs={{ span: 24 }}
                      >
                        <Form.Item
                          label="Tipo de Documento"
                          name="document_type"
                          initialValue="CPF"
                        >
                          <Select size="large" onChange={setDocumentType}>
                            <Select.Option value="CPF">CPF</Select.Option>
                            <Select.Option value="CNPJ">CNPJ</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col
                        lg={{ span: 12 }}
                        md={{ span: 12 }}
                        sm={{ span: 24 }}
                        xs={{ span: 24 }}
                      >
                        <Form.Item
                          label="Documento"
                          name="document"
                          rules={[
                            { required: true, message: "Informe o documento" },
                          ]}
                        >
                          <InputMask
                            mask={
                              documentType === "CPF"
                                ? "999.999.999-99"
                                : "99.999.999/9999-99"
                            }
                          >
                            <Input size="large" />
                          </InputMask>
                        </Form.Item>
                      </Col>
                      <Col
                        lg={{ span: 12 }}
                        md={{ span: 12 }}
                        sm={{ span: 24 }}
                        xs={{ span: 24 }}
                      >
                        <Form.Item
                          label="Email"
                          name="email"
                          rules={[
                            {
                              required: true,
                              type: "email",
                              message: "Email inválido",
                            },
                          ]}
                        >
                          <Input size="large" />
                        </Form.Item>
                      </Col>
                      <Col
                        lg={{ span: 12 }}
                        md={{ span: 12 }}
                        sm={{ span: 24 }}
                        xs={{ span: 24 }}
                      >
                        <Form.Item
                          label="Telefone"
                          name="phone"
                          rules={[
                            { required: true, message: "Informe o telefone" },
                          ]}
                        >
                          <InputMask mask="(99) 99999-9999">
                            <Input size="large" />
                          </InputMask>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Divider orientation="left">Endereço</Divider>

                    <Row gutter={16}>
                      <Col
                        lg={{ span: 8 }}
                        md={{ span: 8 }}
                        sm={{ span: 24 }}
                        xs={{ span: 24 }}
                      >
                        <Form.Item
                          label="CEP"
                          name="postal_code"
                          rules={[{ required: true, message: "Informe o CEP" }]}
                        >
                          <InputMask
                            mask="99999-999"
                            onChange={handleCepChange}
                          >
                            <Input size="large" />
                          </InputMask>
                        </Form.Item>
                      </Col>
                      <Col
                        lg={{ span: 16 }}
                        md={{ span: 16 }}
                        sm={{ span: 24 }}
                        xs={{ span: 24 }}
                      >
                        <Form.Item
                          label="Rua"
                          name="address"
                          rules={[{ required: true, message: "Informe a rua" }]}
                        >
                          <Input size="large" />
                        </Form.Item>
                      </Col>
                      <Col
                        lg={{ span: 8 }}
                        md={{ span: 8 }}
                        sm={{ span: 24 }}
                        xs={{ span: 24 }}
                      >
                        <Form.Item
                          label="Número"
                          name="number"
                          rules={[
                            { required: true, message: "Informe o número" },
                          ]}
                        >
                          <Input size="large" />
                        </Form.Item>
                      </Col>
                      <Col
                        lg={{ span: 16 }}
                        md={{ span: 16 }}
                        sm={{ span: 24 }}
                        xs={{ span: 24 }}
                      >
                        <Form.Item label="Bairro" name="neighborhood">
                          <Input size="large" />
                        </Form.Item>
                      </Col>
                      <Col
                        lg={{ span: 12 }}
                        md={{ span: 12 }}
                        sm={{ span: 24 }}
                        xs={{ span: 24 }}
                      >
                        <Form.Item label="Cidade" name="city">
                          <Input size="large" />
                        </Form.Item>
                      </Col>
                      <Col
                        lg={{ span: 12 }}
                        md={{ span: 12 }}
                        sm={{ span: 24 }}
                        xs={{ span: 24 }}
                      >
                        <Form.Item label="Estado" name="state">
                          <Input size="large" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Panel>
                  <Panel header="Dados de pagamento" key="2">
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          label="Forma de Pagamento"
                          name="payment_method"
                          rules={[
                            {
                              required: true,
                              message: "Selecione uma forma de pagamento",
                            },
                          ]}
                        >
                          <Select
                            size="large"
                            placeholder="Selecione"
                            options={availablePaymentMethods}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {paymentMethod === "CreditCard" && (
                      <>
                        <Divider orientation="left">Cartão de Crédito</Divider>
                        <Row gutter={16}>
                          <Col span={24}>
                            <Form.Item
                              label="Nome no Cartão"
                              name="card_holder_name"
                              rules={[
                                {
                                  required: true,
                                  message: "Informe o nome impresso no cartão",
                                },
                              ]}
                            >
                              <Input
                                size="large"
                                placeholder="Como aparece no cartão"
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              label="Número do Cartão"
                              name="card_number"
                              rules={[
                                {
                                  required: true,
                                  message: "Informe o número do cartão",
                                },
                              ]}
                            >
                              <InputMask mask="9999.9999.9999.9999">
                                <Input
                                  size="large"
                                  placeholder="0000.0000.0000.0000"
                                />
                              </InputMask>
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              label="Validade"
                              name="card_expiration_date"
                              rules={[
                                {
                                  required: true,
                                  message: "Informe a validade",
                                },
                              ]}
                            >
                              <InputMask mask="99/99">
                                <Input size="large" placeholder="MM/AA" />
                              </InputMask>
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              label="CVV"
                              name="card_cvv"
                              rules={[
                                { required: true, message: "Informe o CVV" },
                              ]}
                            >
                              <InputMask mask="999">
                                <Input size="large" placeholder="123" />
                              </InputMask>
                            </Form.Item>
                          </Col>
                        </Row>
                      </>
                    )}
                  </Panel>
                </Collapse>

                <div style={{ marginTop: 24, textAlign: "right" }}>
                  <Space>
                    <Button size="large" onClick={() => navigate(-1)}>
                      Voltar
                    </Button>
                    <Button
                      type="primary"
                      size="large"
                      htmlType="submit"
                      loading={loadingSubmit}
                    >
                      Contratar
                    </Button>
                  </Space>
                </div>
              </Form>
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );
}
